// Single source of truth (frontend mirror of backend gst_status_catalog.py).
// key: [displayName, description, phase, tone]
export const GST_STATUS_CATALOG = {
  // Documents
  documents_pending: ['Documents Pending', 'No documents uploaded', 'documents', 'warn'],
  partially_uploaded: ['Partially Uploaded', 'Some documents uploaded', 'documents', 'warn'],
  under_review: ['Under Review', 'Admin starts checking', 'documents', 'info'],
  documents_required: ['Documents Required', 'Admin requests more documents with remark', 'documents', 'danger'],
  proceeded_with_available: ['Proceed with Available Documents', 'Admin continues without all optional documents', 'documents', 'progress'],
  documents_approved: ['Documents Approved', 'Admin accepts the available/required documents', 'documents', 'success'],
  documents_overdue: ['Documents Overdue', 'Due date passes and required documents remain missing', 'documents', 'danger'],
  // Preparation & confirmation
  admin_preparing: ['Admin Preparing', 'Admin starts preparing return', 'preparation', 'progress'],
  client_confirmation_pending: ['Client Confirmation Pending', 'Admin sends Excel/template to client', 'preparation', 'info'],
  client_changes_requested: ['Changes Requested', 'Client rejects/requests changes with remark', 'preparation', 'danger'],
  admin_resubmitted: ['Admin Resubmitted', 'Admin uploads a new version', 'preparation', 'progress'],
  client_approved: ['Approved by Client', 'Client approves latest version', 'preparation', 'success'],
  ready_for_filing: ['Ready for Filing', 'Approved version is available', 'preparation', 'success'],
  // Filing
  not_started: ['Not Started', 'Filing period created', 'filing', 'neutral'],
  in_progress: ['In Progress', 'Admin begins work', 'filing', 'progress'],
  filed: ['Filed', 'Admin marks the return filed', 'filing', 'success'],
  acknowledgement_pending: ['Acknowledgement Pending', 'Filed but proof not uploaded', 'filing', 'warn'],
  filed_complete: ['Complete', 'Acknowledgement and required proof uploaded', 'filing', 'success'],
  filing_overdue: ['Filing Overdue', 'Due date passes before filing', 'filing', 'danger'],
  // Tax
  tax_payment_due: ['Tax Payment Due', 'Tax liability is payable', 'tax', 'warn'],
  tax_reminder_sent: ['Tax Reminder Sent', 'Admin sends payment email', 'tax', 'info'],
  tax_payment_overdue: ['Tax Payment Overdue', 'Payment due date passes', 'tax', 'danger'],
  tax_paid: ['Tax Paid', 'Admin marks tax paid', 'tax', 'success'],
  tax_not_applicable: ['Tax Not Applicable', 'No tax payment is required', 'tax', 'neutral'],
  // NIL
  nil_confirmation_pending: ['NIL Confirmation Pending', 'NIL option is available', 'nil', 'warn'],
  nil_confirmed_by_client: ['NIL Confirmed by Client', 'Client confirms NIL', 'nil', 'success'],
  nil_confirmed_by_admin: ['NIL Confirmed by Admin', 'Admin confirms NIL', 'nil', 'success'],
  nil_filed: ['NIL Filed', 'Admin files a NIL return', 'nil', 'success'],
  nil_changes_requested: ['NIL Review Required', 'Transactions or corrections are found', 'nil', 'danger'],
  nil_cancelled: ['NIL Cancelled', 'Admin cancels NIL with remark', 'nil', 'neutral'],
  // LUT
  lut_not_required: ['LUT Not Required', 'Client does not use LUT', 'lut', 'neutral'],
  lut_renewal_due: ['LUT Renewal Due', 'New financial year is approaching', 'lut', 'warn'],
  lut_documents_pending: ['LUT Documents Pending', 'Client information is missing', 'lut', 'warn'],
  lut_under_preparation: ['LUT Under Preparation', 'Admin prepares RFD-11', 'lut', 'progress'],
  lut_client_confirmation_pending: ['Client Confirmation Pending', 'Client must confirm/sign', 'lut', 'info'],
  lut_changes_requested: ['Changes Requested', 'Client requests correction', 'lut', 'danger'],
  lut_ready_for_filing: ['Ready for Filing', 'Client confirms', 'lut', 'success'],
  lut_filed: ['Filed', 'Admin files LUT', 'lut', 'success'],
  lut_active: ['Active', 'ARN uploaded', 'lut', 'success'],
  lut_expired: ['Expired', 'Validity ends without renewal', 'lut', 'danger'],
};

// Legacy → canonical.
export const STATUS_ALIASES = {
  documents_awaited: 'documents_pending',
  overdue: 'documents_overdue',
  nil_check_pending: 'nil_confirmation_pending',
  under_preparation: 'admin_preparing',
  summary_prepared: 'admin_preparing',
  sent_for_client_approval: 'client_confirmation_pending',
  changes_requested: 'client_changes_requested',
  approved_by_client: 'client_approved',
  tax_pending: 'tax_reminder_sent',
  complete: 'filed_complete',
  filed_acknowledgement_pending: 'acknowledgement_pending',
  nil_selected: 'nil_confirmed_by_admin',
  nil_summary_sent: 'nil_confirmation_pending',
  nil_client_approved: 'nil_confirmed_by_client',
  nil_ready_for_filing: 'ready_for_filing',
  nil_overdue: 'filing_overdue',
  late: 'filing_overdue',
  cancelled: 'nil_cancelled',
};

const TONE_CLS = {
  neutral: 'bg-slate-600/30 text-slate-300',
  info: 'bg-blue-500/20 text-blue-300',
  progress: 'bg-indigo-500/20 text-indigo-300',
  warn: 'bg-amber-500/20 text-amber-300',
  danger: 'bg-red-500/20 text-red-300',
  success: 'bg-emerald-500/20 text-emerald-300',
};

export function gstStatusMeta(status) {
  const canonical = GST_STATUS_CATALOG[status] ? status : (STATUS_ALIASES[status] || status);
  const entry = GST_STATUS_CATALOG[canonical];
  if (entry) {
    const [label, description, phase, tone] = entry;
    return { key: canonical, label, description, phase, tone, cls: TONE_CLS[tone] };
  }
  return {
    key: canonical,
    label: (status || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) || 'Unknown',
    description: '', phase: 'other', tone: 'neutral', cls: TONE_CLS.neutral,
  };
}

// Statuses that are terminal or already overdue — never re-derive overdue for these.
const TERMINAL_KEYS = new Set([
  'filed', 'filed_complete', 'nil_filed', 'nil_cancelled', 'lut_filed', 'lut_active',
  'documents_overdue', 'filing_overdue', 'tax_payment_overdue', 'lut_expired',
  'not_required', 'tax_not_applicable',
]);
// Document-collection phase → an overdue here surfaces as "Documents Overdue".
const DOC_PHASE_KEYS = new Set([
  'not_started', 'documents_pending', 'partially_uploaded', 'documents_required', 'under_review',
]);

// Overdue is driven by the DUE DATE (workflow rule), not by "no file exists".
// Returns the effective status to badge for a filing object.
export function effectiveFilingStatus(f) {
  if (!f) return 'not_started';
  const meta = gstStatusMeta(f.status);
  if (TERMINAL_KEYS.has(meta.key)) return meta.key;
  if (f.nil_filing?.selected) return meta.key;
  const today = new Date().toISOString().slice(0, 10);
  const due = f.due_date || f.client_due_date;
  if (due && String(due).slice(0, 10) < today) {
    if (meta.phase === 'tax') return 'tax_payment_overdue';
    return DOC_PHASE_KEYS.has(meta.key) ? 'documents_overdue' : 'filing_overdue';
  }
  return meta.key;
}
